import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import inventoryService from '../../services/inventoryService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash, CaretDown, CaretUp, Check, X as XIcon } from '@phosphor-icons/react';

const emptyForm = { name: '', category_id: '', unit: '', quantity_on_hand: 0, reorder_level: '', location: '', unit_cost: '', gl_expense_account_code: '', gl_inventory_asset_account_code: '', notes: '' };
const emptyIssueForm = { issued_to: '', quantity: '', issue_date: new Date().toISOString().split('T')[0], purpose: '' };

function StockItemsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [issuancesByItem, setIssuancesByItem] = useState({});
  const [issueForm, setIssueForm] = useState(emptyIssueForm);
  const [issuing, setIssuing] = useState(false);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredItems(items);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredItems(items.filter((i) => i.name.toLowerCase().includes(term)));
  }, [items, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsData, categoriesData] = await Promise.all([
        inventoryService.listStockItems(),
        inventoryService.listCategories().catch(() => []),
      ]);
      setItems(Array.isArray(itemsData) ? itemsData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      toast.error('Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => categories.find((c) => c.id === categoryId)?.name || '-';

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name,
      category_id: item.category_id || '',
      unit: item.unit,
      quantity_on_hand: item.quantity_on_hand,
      reorder_level: item.reorder_level || '',
      location: item.location || '',
      unit_cost: item.unit_cost ?? '',
      gl_expense_account_code: item.gl_expense_account_code || '',
      gl_inventory_asset_account_code: item.gl_inventory_asset_account_code || '',
      notes: item.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.unit) {
      toast.error('Name and unit are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        category_id: formData.category_id || null,
        reorder_level: formData.reorder_level ? Number(formData.reorder_level) : null,
        unit_cost: formData.unit_cost !== '' ? Number(formData.unit_cost) : null,
        gl_expense_account_code: formData.gl_expense_account_code || null,
        gl_inventory_asset_account_code: formData.gl_inventory_asset_account_code || null,
      };
      if (editingId) {
        const { quantity_on_hand, ...rest } = payload;
        await inventoryService.updateStockItem(editingId, rest);
        toast.success('Stock item updated');
      } else {
        await inventoryService.createStockItem({ ...payload, quantity_on_hand: Number(formData.quantity_on_hand) || 0 });
        toast.success('Stock item created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save stock item');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await inventoryService.deleteStockItem(deleteTarget);
      toast.success('Stock item deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete stock item');
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleExpand = async (item) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(item.id);
    setIssueForm(emptyIssueForm);
    try {
      const issuances = await inventoryService.listIssuances(item.id);
      setIssuancesByItem((prev) => ({ ...prev, [item.id]: Array.isArray(issuances) ? issuances : [] }));
    } catch (error) {
      toast.error('Failed to load issuance log');
    }
  };

  const handleIssue = async (item) => {
    if (!issueForm.issued_to || !issueForm.quantity) {
      toast.error('Issued-to and quantity are required');
      return;
    }
    setIssuing(true);
    try {
      const result = await inventoryService.issueStock(item.id, {
        ...issueForm,
        quantity: Number(issueForm.quantity),
      });
      toast.success('Stock issued');
      setItems((prev) => prev.map((i) => (i.id === item.id ? result.stock_item : i)));
      const issuances = await inventoryService.listIssuances(item.id);
      setIssuancesByItem((prev) => ({ ...prev, [item.id]: Array.isArray(issuances) ? issuances : [] }));
      setIssueForm(emptyIssueForm);
    } catch (error) {
      toast.error(error.message || 'Failed to issue stock');
    } finally {
      setIssuing(false);
    }
  };

  const handleApproveIssuance = async (item, issuance) => {
    try {
      const result = await inventoryService.approveIssuance(item.id, issuance.id);
      toast.success('Issuance approved');
      setItems((prev) => prev.map((i) => (i.id === item.id ? result.stock_item : i)));
      const issuances = await inventoryService.listIssuances(item.id);
      setIssuancesByItem((prev) => ({ ...prev, [item.id]: Array.isArray(issuances) ? issuances : [] }));
    } catch (error) {
      toast.error(error.message || 'Failed to approve issuance');
    }
  };

  const handleRejectIssuance = (item, issuance) => {
    setPromptDialog({
      title: 'Reject Issuance',
      message: 'Reason for rejecting this issuance (optional):',
      defaultValue: '',
      onSubmit: async (value) => {
        const reason = value || undefined;
        try {
          await inventoryService.rejectIssuance(item.id, issuance.id, reason);
          toast.success('Issuance rejected');
          const issuances = await inventoryService.listIssuances(item.id);
          setIssuancesByItem((prev) => ({ ...prev, [item.id]: Array.isArray(issuances) ? issuances : [] }));
        } catch (error) {
          toast.error(error.message || 'Failed to reject issuance');
        }
      },
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading stock items...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Inventory — Stock Items</h1>
            <p>Consumables tracked by quantity, with an issuance log</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Add Stock Item
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          {filteredItems.length === 0 ? (
            <div className="empty-state">
              <p>No stock items found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Qty on Hand</th>
                  <th>Unit</th>
                  <th>Reorder Level</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <button className="btn-icon" onClick={() => toggleExpand(item)} title="Expand">
                          {expandedId === item.id ? <CaretUp size={16} /> : <CaretDown size={16} />}
                        </button>
                      </td>
                      <td>{item.name}</td>
                      <td>{getCategoryName(item.category_id)}</td>
                      <td>
                        {item.quantity_on_hand}
                        {item.reorder_level != null && item.quantity_on_hand <= item.reorder_level && (
                          <span style={{ color: '#dc2626', marginLeft: 6, fontSize: 12 }}>(low)</span>
                        )}
                      </td>
                      <td>{item.unit}</td>
                      <td>{item.reorder_level ?? '-'}</td>
                      <td className="action-buttons">
                        <button className="btn-icon" title="Edit" onClick={() => openEditModal(item)}>
                          <PencilSimple size={18} />
                        </button>
                        <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(item.id)}>
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                    {expandedId === item.id && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--muted, #f8f9fa)', padding: 16 }}>
                          <h4 style={{ margin: '0 0 8px' }}>Issuance Log</h4>
                          {(issuancesByItem[item.id] || []).length === 0 ? (
                            <p style={{ color: '#666' }}>No issuances recorded yet.</p>
                          ) : (
                            <ul style={{ marginBottom: 16, paddingLeft: 0, listStyle: 'none' }}>
                              {(issuancesByItem[item.id] || []).map((iss) => (
                                <li key={iss.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                                  <span>
                                    {iss.quantity} {item.unit} to {iss.issued_to} on {iss.issue_date}{iss.purpose ? ` — ${iss.purpose}` : ''}
                                    {iss.gl_journal_entry_id && <span style={{ color: '#16a34a', fontSize: 12 }}> (posted to GL)</span>}
                                  </span>
                                  {iss.approval_status === 'pending' && (
                                    <span className="badge inactive" style={{ fontSize: 11 }}>Pending Approval</span>
                                  )}
                                  {iss.approval_status === 'rejected' && (
                                    <span className="badge" style={{ fontSize: 11, background: '#fee2e2', color: '#dc2626' }} title={iss.rejection_reason || ''}>Rejected</span>
                                  )}
                                  {iss.approval_status === 'pending' && iss.issued_by_staff_id !== user?.id && (
                                    <>
                                      <button className="btn-icon" title="Approve" onClick={() => handleApproveIssuance(item, iss)}>
                                        <Check size={16} color="#16a34a" />
                                      </button>
                                      <button className="btn-icon btn-danger" title="Reject" onClick={() => handleRejectIssuance(item, iss)}>
                                        <XIcon size={16} />
                                      </button>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                          <h4 style={{ margin: '0 0 8px' }}>Issue Stock</h4>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Issued To</label>
                              <input type="text" value={issueForm.issued_to} onChange={(e) => setIssueForm({ ...issueForm, issued_to: e.target.value })} placeholder="e.g., Mr. Mensah" />
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Quantity</label>
                              <input type="number" min="0" step="0.01" value={issueForm.quantity} onChange={(e) => setIssueForm({ ...issueForm, quantity: e.target.value })} style={{ width: 100 }} />
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Date</label>
                              <input type="date" value={issueForm.issue_date} onChange={(e) => setIssueForm({ ...issueForm, issue_date: e.target.value })} />
                            </div>
                            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 160 }}>
                              <label>Purpose</label>
                              <input type="text" value={issueForm.purpose} onChange={(e) => setIssueForm({ ...issueForm, purpose: e.target.value })} />
                            </div>
                            <button className="btn btn-primary" onClick={() => handleIssue(item)} disabled={issuing}>
                              {issuing ? 'Issuing...' : 'Issue'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Stock Item' : 'Add Stock Item'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Unit *</label>
                  <input type="text" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} placeholder="e.g., box, piece" required />
                </div>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select value={formData.category_id} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}>
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {!editingId && (
                <div className="form-group">
                  <label>Initial Quantity on Hand</label>
                  <input type="number" min="0" step="0.01" value={formData.quantity_on_hand} onChange={(e) => setFormData({ ...formData, quantity_on_hand: e.target.value })} />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Reorder Level</label>
                  <input type="number" min="0" value={formData.reorder_level} onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unit Cost (GHS)</label>
                  <input type="number" min="0" step="0.01" value={formData.unit_cost} onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })} placeholder="e.g., 25.00" />
                </div>
                <div className="form-group">
                  <label>GL Expense Account Code</label>
                  <input type="text" value={formData.gl_expense_account_code} onChange={(e) => setFormData({ ...formData, gl_expense_account_code: e.target.value })} placeholder="e.g., 5300" />
                </div>
                <div className="form-group">
                  <label>GL Inventory Asset Account Code</label>
                  <input type="text" value={formData.gl_inventory_asset_account_code} onChange={(e) => setFormData({ ...formData, gl_inventory_asset_account_code: e.target.value })} placeholder="e.g., 1250" />
                </div>
              </div>
              <p style={{ fontSize: 12, color: '#666', marginTop: -8, marginBottom: 12 }}>
                When all three are set, issuing this item posts a journal entry to your chart of accounts.
              </p>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Stock Item"
        message="This will permanently delete this stock item and its issuance log. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="info"
        input={{ placeholder: 'Reason (optional)', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default StockItemsPage;
